import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { updateFavicon } from '@/lib/favicon';

interface PlatformBranding {
  id: string;
  platform_name: string;
  logo_url: string | null;
  dark_logo_url: string | null;
  favicon_url: string | null;
  primary_color: string;
  secondary_color: string;
  heading_font: string;
  body_font: string;
  contact_link: string | null;
  contact_text: string | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
  og_image_url: string | null;
  whatsapp_template_ar: string | null;
  whatsapp_template_en: string | null;
  whatsapp_template_delivery_ar: string | null;
  whatsapp_template_delivery_en: string | null;
  whatsapp_template_pickup_ar: string | null;
  whatsapp_template_pickup_en: string | null;
  hubspot_enabled?: boolean;
  hubspot_portal_id?: string | null;
  platform_url?: string | null;
  custom_heading_font_url?: string | null;
  custom_body_font_url?: string | null;
  custom_heading_font_name?: string | null;
  custom_body_font_name?: string | null;
}

// ─── Global singleton cache ───
let _cachedBranding: PlatformBranding | null = null;

/** Sync getter — returns cached branding or null (used by print system) */
export function getCachedPlatformBranding(): PlatformBranding | null {
  return _cachedBranding;
}
let _fetchPromise: Promise<PlatformBranding | null> | null = null;
let _listeners: Set<() => void> = new Set();
let _domApplied = false;

function notifyListeners() {
  _listeners.forEach(fn => fn());
}

// ─── Color helpers ───
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  hex = hex.replace(/^#/, '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslToString(hsl: { h: number; s: number; l: number }): string {
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
}

function isLightColor(hsl: { h: number; s: number; l: number }): boolean {
  return hsl.l > 50;
}

function applyBrandingToDOM(branding: PlatformBranding) {
  const primaryHsl = hexToHsl(branding.primary_color);
  const secondaryHsl = hexToHsl(branding.secondary_color);

  const accentFgHsl = { ...primaryHsl, l: Math.max(primaryHsl.l - 15, 20) };
  const pH = primaryHsl.h;
  const pS = primaryHsl.s;
  const sH = secondaryHsl.h;
  const sS = secondaryHsl.s;

  // ─── Light mode values ───
  const lightPrimaryFg = isLightColor(primaryHsl) ? '0 0% 0%' : '0 0% 100%';
  const lightVars = `
    --primary: ${hslToString(primaryHsl)};
    --primary-foreground: ${lightPrimaryFg};
    --ring: ${hslToString(primaryHsl)};
    --accent: ${hslToString(secondaryHsl)};
    --accent-foreground: ${hslToString(accentFgHsl)};
    --muted: ${hslToString({ h: pH, s: Math.min(pS, 20), l: 95 })};
    --muted-foreground: ${hslToString({ h: pH, s: Math.min(pS, 15), l: 45 })};
    --secondary: ${hslToString({ h: pH, s: Math.min(pS, 40), l: 96 })};
    --secondary-foreground: ${hslToString({ h: pH, s: Math.min(pS, 10), l: 15 })};
    --border: ${hslToString({ h: pH, s: Math.min(pS, 15), l: 90 })};
    --input: ${hslToString({ h: pH, s: Math.min(pS, 15), l: 90 })};
    --sidebar-background: ${hslToString({ h: pH, s: Math.min(pS, 30), l: 98 })};
    --sidebar-foreground: ${hslToString({ h: pH, s: Math.min(pS, 10), l: 25 })};
    --sidebar-primary: ${hslToString(primaryHsl)};
    --sidebar-primary-foreground: ${lightPrimaryFg};
    --sidebar-accent: ${hslToString(secondaryHsl)};
    --sidebar-accent-foreground: ${hslToString(accentFgHsl)};
    --sidebar-border: ${hslToString({ h: pH, s: Math.min(pS, 15), l: 90 })};
    --sidebar-ring: ${hslToString(primaryHsl)};
  `;

  // ─── Dark mode values — derived from brand colors ───
  const darkPrimary = { h: sH, s: Math.min(sS, 85), l: Math.max(secondaryHsl.l, 55) };
  const darkPrimaryFg = isLightColor(darkPrimary) ? '0 0% 0%' : '0 0% 100%';
  const darkVars = `
    --background: ${pH} ${Math.min(pS, 14)}% 7%;
    --foreground: ${pH} 20% 95%;
    --card: ${pH} ${Math.min(pS, 14)}% 10%;
    --card-foreground: ${pH} 20% 95%;
    --popover: ${pH} ${Math.min(pS, 14)}% 10%;
    --popover-foreground: ${pH} 20% 95%;
    --primary: ${hslToString(darkPrimary)};
    --primary-foreground: ${darkPrimaryFg};
    --ring: ${hslToString(darkPrimary)};
    --accent: ${sH} ${Math.min(sS, 60)}% 22%;
    --accent-foreground: ${sH} 50% 85%;
    --muted: ${pH} ${Math.min(pS, 14)}% 15%;
    --muted-foreground: ${pH} 15% 55%;
    --secondary: ${pH} ${Math.min(pS, 20)}% 15%;
    --secondary-foreground: ${pH} 20% 95%;
    --border: ${pH} ${Math.min(pS, 14)}% 18%;
    --input: ${pH} ${Math.min(pS, 14)}% 18%;
    --destructive: 0 62% 50%;
    --destructive-foreground: 0 0% 95%;
    --success: 142 76% 42%;
    --success-foreground: 0 0% 100%;
    --warning: 38 92% 55%;
    --warning-foreground: 0 0% 100%;
    --sidebar-background: ${pH} ${Math.min(pS, 15)}% 6%;
    --sidebar-foreground: ${pH} 20% 90%;
    --sidebar-primary: ${hslToString(darkPrimary)};
    --sidebar-primary-foreground: ${darkPrimaryFg};
    --sidebar-accent: ${sH} ${Math.min(sS, 50)}% 18%;
    --sidebar-accent-foreground: ${sH} 50% 85%;
    --sidebar-border: ${pH} ${Math.min(pS, 14)}% 15%;
    --sidebar-ring: ${hslToString(darkPrimary)};
    color-scheme: dark;
  `;

  // ─── Inject as <style> so .dark selector works automatically ───
  const styleId = 'dynamic-branding-vars';
  let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
    :root { ${lightVars} }
    .dark { ${darkVars} }
  `;

  // ─── Fonts ───
  // Helper: inject @font-face for custom fonts
  const injectCustomFontFace = (name: string, url: string) => {
    const styleId = `custom-font-face-${name.replace(/\s/g, '-')}`;
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `@font-face { font-family: "${name}"; src: url("${url}"); font-display: swap; }`;
      document.head.appendChild(style);
    }
  };

  // Load custom fonts via @font-face if present, otherwise Google Fonts
  const hasCustomHeading = branding.custom_heading_font_url && branding.custom_heading_font_name;
  const hasCustomBody = branding.custom_body_font_url && branding.custom_body_font_name;

  if (hasCustomHeading) {
    injectCustomFontFace(branding.custom_heading_font_name!, branding.custom_heading_font_url!);
  }
  if (hasCustomBody) {
    injectCustomFontFace(branding.custom_body_font_name!, branding.custom_body_font_url!);
  }

  // Load Google Fonts for non-custom fonts
  if (branding.heading_font || branding.body_font) {
    const googleFontsToLoad: string[] = [];
    if (!hasCustomHeading && branding.heading_font) googleFontsToLoad.push(branding.heading_font);
    if (!hasCustomBody && branding.body_font) googleFontsToLoad.push(branding.body_font);
    
    const uniqueFonts = [...new Set(googleFontsToLoad)];
    uniqueFonts.forEach(font => {
      if (font) {
        const linkId = `google-font-${font.replace(/\s/g, '-')}`;
        if (!document.getElementById(linkId)) {
          const link = document.createElement('link');
          link.id = linkId;
          link.href = `https://fonts.googleapis.com/css2?family=${font.replace(/\s/g, '+')}:wght@300;400;500;600;700&display=swap`;
          link.rel = 'stylesheet';
          document.head.appendChild(link);
        }
      }
    });
    
    const effectiveHeadingFont = hasCustomHeading ? branding.custom_heading_font_name! : branding.heading_font;
    if (effectiveHeadingFont) {
      const headingFontStack = `"${effectiveHeadingFont}", "Noto Sans Arabic", sans-serif`;
      document.documentElement.style.setProperty('--font-heading', headingFontStack);
      const style = document.getElementById('dynamic-heading-font') || document.createElement('style');
      style.id = 'dynamic-heading-font';
      style.textContent = `
        h1, h2, h3, h4, h5, h6, .font-heading {
          font-family: ${headingFontStack} !important;
        }
      `;
      if (!document.getElementById('dynamic-heading-font')) {
        document.head.appendChild(style);
      }
    }
    
    const effectiveBodyFont = hasCustomBody ? branding.custom_body_font_name! : branding.body_font;
    if (effectiveBodyFont) {
      const bodyFontStack = `"${effectiveBodyFont}", "Noto Sans Arabic", sans-serif`;
      document.documentElement.style.setProperty('--font-body', bodyFontStack);
      document.documentElement.style.fontFamily = bodyFontStack;
      document.body.style.fontFamily = bodyFontStack;
    }
  }
  
  // ─── Favicon ───
  // Favicon is now served from static root files (favicon.ico, favicon.png, etc.)
  // defined in index.html. We no longer inject external URLs to maintain
  // Google Search compatibility. See src/lib/favicon.ts for details.
  
  if (branding.platform_name && !document.title.includes(branding.platform_name)) {
    document.title = branding.seo_title || branding.platform_name;
  }

  // ─── SEO Meta Tags ───
  const setMeta = (selector: string, content: string, attr = 'content') => {
    const el = document.querySelector(selector) as HTMLMetaElement | null;
    if (el) el.setAttribute(attr, content);
  };

  const pageTitle = branding.seo_title || branding.platform_name;
  const pageDesc = branding.seo_description || '';

  if (pageTitle) {
    setMeta('meta[property="og:title"]', pageTitle);
    setMeta('meta[name="twitter:title"]', pageTitle);
  }
  if (pageDesc) {
    setMeta('meta[name="description"]', pageDesc);
    setMeta('meta[property="og:description"]', pageDesc);
    setMeta('meta[name="twitter:description"]', pageDesc);
  }
  if (branding.seo_keywords) {
    let keywordsMeta = document.querySelector('meta[name="keywords"]') as HTMLMetaElement | null;
    if (!keywordsMeta) {
      keywordsMeta = document.createElement('meta');
      keywordsMeta.name = 'keywords';
      document.head.appendChild(keywordsMeta);
    }
    keywordsMeta.content = branding.seo_keywords;
  }
  if (branding.og_image_url) {
    setMeta('meta[property="og:image"]', branding.og_image_url);
    setMeta('meta[name="twitter:image"]', branding.og_image_url);
  }

  _domApplied = true;
}

// Single shared fetch — deduplicates concurrent calls
async function fetchBranding(): Promise<PlatformBranding | null> {
  if (_cachedBranding) return _cachedBranding;
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('platform_branding')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        _cachedBranding = {
          id: data.id,
          platform_name: data.platform_name,
          logo_url: data.logo_url,
          dark_logo_url: (data as any).dark_logo_url || null,
          favicon_url: data.favicon_url,
          primary_color: data.primary_color || '#EA580C',
          secondary_color: data.secondary_color || '#FFF7ED',
          heading_font: data.heading_font || 'Noto Sans Arabic',
          body_font: data.body_font || 'Noto Sans Arabic',
          contact_link: data.contact_link,
          contact_text: data.contact_text,
          seo_title: data.seo_title,
          seo_description: data.seo_description,
          seo_keywords: data.seo_keywords,
          og_image_url: data.og_image_url,
          whatsapp_template_ar: (data as any).whatsapp_template_ar || null,
          whatsapp_template_en: (data as any).whatsapp_template_en || null,
          whatsapp_template_delivery_ar: (data as any).whatsapp_template_delivery_ar || null,
          whatsapp_template_delivery_en: (data as any).whatsapp_template_delivery_en || null,
          whatsapp_template_pickup_ar: (data as any).whatsapp_template_pickup_ar || null,
          whatsapp_template_pickup_en: (data as any).whatsapp_template_pickup_en || null,
          hubspot_enabled: (data as any).hubspot_enabled ?? false,
          hubspot_portal_id: (data as any).hubspot_portal_id || null,
          platform_url: data.platform_url || null,
          custom_heading_font_url: (data as any).custom_heading_font_url || null,
          custom_body_font_url: (data as any).custom_body_font_url || null,
          custom_heading_font_name: (data as any).custom_heading_font_name || null,
          custom_body_font_name: (data as any).custom_body_font_name || null,
        };
        if (!_domApplied) applyBrandingToDOM(_cachedBranding);
        notifyListeners();
        return _cachedBranding;
      }
      return null;
    } catch (err) {
      console.error('Error loading platform branding:', err);
      return null;
    } finally {
      _fetchPromise = null;
    }
  })();

  return _fetchPromise;
}

export function usePlatformBranding() {
  const [branding, setBranding] = useState<PlatformBranding | null>(_cachedBranding);
  const [isLoading, setIsLoading] = useState(!_cachedBranding);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // If already cached, just use it and apply DOM if needed
    if (_cachedBranding) {
      setBranding(_cachedBranding);
      setIsLoading(false);
      if (!_domApplied) applyBrandingToDOM(_cachedBranding);
      return;
    }

    // Subscribe to updates from other instances
    const listener = () => {
      if (_cachedBranding) {
        setBranding(_cachedBranding);
        setIsLoading(false);
      }
    };
    _listeners.add(listener);

    // Trigger shared fetch
    fetchBranding().then(result => {
      if (result) {
        setBranding(result);
      }
      setIsLoading(false);
    }).catch(err => {
      setError(err instanceof Error ? err : new Error('Failed to load branding'));
      setIsLoading(false);
    });

    return () => {
      _listeners.delete(listener);
    };
  }, []);

  const refreshBranding = async () => {
    setIsLoading(true);
    // Clear cache to force re-fetch
    _cachedBranding = null;
    _fetchPromise = null;
    _domApplied = false;

    try {
      const result = await fetchBranding();
      if (result) {
        applyBrandingToDOM(result);
        setBranding(result);
      }
    } catch (err) {
      console.error('Error refreshing platform branding:', err);
      setError(err instanceof Error ? err : new Error('Failed to refresh branding'));
    } finally {
      setIsLoading(false);
    }
  };

  return { branding, isLoading, error, refreshBranding };
}
